import React, { useState, useEffect, useRef } from 'react'
import { Typography, Box, Card, CardContent, CardMedia } from '@mui/material'
import { createRoot } from 'react-dom/client'

let activeNotifications = []

const recalculateNotificationPositions = () => {
  const padding = 10 
  let currentTop = 20 

  activeNotifications.forEach((notification) => {
    if (notification.containerEl) {
      const height = notification.containerEl.offsetHeight
      notification.containerEl.style.top = `${currentTop}px`
      currentTop += height + padding
    }
  })
}

export const addAnimationStyles = () => {
  if (document.getElementById('push-notification-animation-styles')) return
  
  const styleEl = document.createElement('style')
  styleEl.id = 'push-notification-animation-styles'
  styleEl.textContent = `
    @keyframes fadeInDown {
      from {
        opacity: 0;
        transform: translateY(-30px) scale(0.95);
        filter: blur(5px);
      }
      30% {
        opacity: 0.8;
        transform: translateY(5px) scale(1.02);
        filter: blur(0);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
        filter: blur(0);
      }
    }
    @keyframes fadeOutUp {
      from {
        opacity: 1;
        transform: translateY(0) scale(1);
        filter: blur(0);
      }
      30% {
        opacity: 0.9;
        transform: translateY(-5px) scale(1.02);
        filter: blur(0);
      }
      to {
        opacity: 0;
        transform: translateY(-30px) scale(0.95);
        filter: blur(5px);
      }
    }
    .fadeInDownAnimation {
      animation: fadeInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards !important;
    }
    .fadeOutUpAnimation {
      animation: fadeOutUp 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards !important;
    }
    @keyframes progressShrink {
      from {
        width: 100%;
      }
      to {
        width: 0%;
      }
    }
    .progress-animation {
      animation: progressShrink 5s linear forwards;
    }
  `
  document.head.appendChild(styleEl)
  return styleEl
}

if (typeof window !== 'undefined') {
  setTimeout(() => {
    addAnimationStyles()
  }, 0)
}

export const showNotification = (notification) => {
  if (typeof window !== 'undefined') {
    addAnimationStyles()
  }
  
  const container = document.createElement('div')
  document.body.appendChild(container)
  
  const notificationId = generateNotificationId()
  const root = createRoot(container)
  
  const handleClose = () => {
    root.unmount()
    document.body.removeChild(container)
  }
  
  root.render(
    <NotificationToast 
      notification={notification} 
      onClose={handleClose} 
      id={notificationId}
    />
  )
  
  return handleClose
}

const NotificationToast = ({ notification, onClose, id }) => {
  const [isClosing, setIsClosing] = useState(false)
  const containerRef = useRef(null)
  
  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      activeNotifications = activeNotifications.filter((n) => n.id !== id)
      recalculateNotificationPositions()
      onClose()
    }, 500)
  }
  
  useEffect(() => {
    if (containerRef.current) {
      const notificationObj = { id, containerEl: containerRef.current }
      activeNotifications.push(notificationObj)
      recalculateNotificationPositions()
    }
    
    const timer = setTimeout(() => {
      handleClose()
    }, 5000)
    
    return () => {
      clearTimeout(timer)
      activeNotifications = activeNotifications.filter((n) => n.id !== id)
      recalculateNotificationPositions()
    }
  }, [id])

  const handleClick = (e) => {
    e.stopPropagation()
    handleClose()
  }

  return (
    <Card 
      ref={containerRef}
      sx={{
        ...styles.toastContainer,
        position: 'fixed', 
        top: 20, 
        right: 20,
      }}
      className={isClosing ? 'fadeOutUpAnimation' : 'fadeInDownAnimation'}
      onClick={handleClick}
    >
      {notification.image ? (
        <CardMedia component="img" sx={styles.toastImage} image={notification.image} alt="" />
      ) : (
        <Box sx={styles.toastDefaultImage}>
          <img 
            src="/logo192.png"
            alt="FEM Fast Logo" 
            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '10px' }}
          />
        </Box>
      )}
      <CardContent sx={styles.toastContent}>
        <Box sx={styles.toastHeader}>
          <Box>
            <Typography component="div" variant="subtitle1" sx={styles.toastTitle}>
              {notification.title || 'FEM Fast'}
            </Typography>
            <Typography variant="subtitle2" sx={styles.toastSource}>
              {window.location.host}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={styles.toastBody}>
          {notification.body || 'New notification'}
        </Typography>
      </CardContent>
      
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '3px',
          overflow: 'hidden',
          borderRadius: '0 0 10px 10px',
          backgroundColor: 'rgba(243, 236, 227, 0.2)',
        }}
      >
        <Box
          className="progress-animation"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            height: '100%',
            backgroundColor: '#EFA282',
            boxShadow: '0 0 8px rgba(239, 162, 130, 0.6)',
            width: '100%',
          }}
        />
      </Box>
    </Card>
  )
}

const styles = {
  toastContainer: {
    maxWidth: 400,
    width: 'calc(100% - 40px)',
    zIndex: 99999,
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
    borderRadius: '10px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: 'rgba(243, 236, 227, 0.7)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    color: '#544f5a',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    border: '1px solid rgba(76, 76, 115, 0.1)',
    backgroundImage: 'none',
    cursor: 'pointer',
    willChange: 'transform, opacity',
    transformOrigin: 'top center',
    position: 'relative',
    minHeight: '80px',
    '&:hover': {
      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
      transform: 'translateY(-2px) scale(1.01)',
      backgroundColor: 'rgba(243, 236, 227, 0.85)',
    },
  },
  
  toastImage: { 
    width: 60, 
    height: 60,
    objectFit: 'cover',
    margin: '10px 0 10px 10px',
    borderRadius: '10px',
    border: '1px solid #EFA282',
    flexShrink: 0,
    boxShadow: '0 3px 8px rgba(80, 80, 80, 0.2)',
  },
  
  toastDefaultImage: {
    width: 60,
    height: 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '10px 0 10px 10px',
    backgroundImage: 'linear-gradient(310deg, rgba(239, 162, 130, 0.2), rgba(243, 236, 227, 0.3))',
    borderRadius: '10px',
    border: '1px solid #EFA282',
    flexShrink: 0,
    boxShadow: '0 3px 8px rgba(239, 162, 130, 0.2)',
  },
  
  toastContent: {
    flex: '1 1 auto', 
    padding: '10px',
    overflow: 'visible',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  
  toastHeader: {
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'flex-start',
    width: '100%',
  },
  
  toastTitle: {
    fontWeight: '700',
    fontFamily: 'Gabarito, tahoma, sans-serif',
    color: '#4C4C73',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    width: '100%',
  },
  
  toastSource: {
    fontSize: '0.8rem', 
    color: '#89868D',
    fontFamily: 'Gabarito, tahoma, sans-serif',
  },
  
  toastBody: {
    mt: 1, 
    color: '#544f5a',
    fontFamily: 'Gabarito, tahoma, sans-serif',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    width: '100%',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.4em',
    maxHeight: '4.2em',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(76, 76, 115, 0.3)',
      borderRadius: '4px',
    },
  },
}

let notificationCounter = 0

export const generateNotificationId = () => {
  return `notification-${Date.now()}-${notificationCounter++}`
}

export default NotificationToast
